import React from 'react';
import { Box, Dialog, DialogContent, Paper, IconButton, Tooltip, Collapse, Link, CircularProgress } from '@mui/material';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { Markdown } from '@usalco/catalyst-component-kit';
import ToolResultRenderer from './ToolResultRenderer';
import { useToast } from '../usalco_lib/ToastContext';
import { UserContext } from '../App';
import { exportResultToEml } from './emailExport';
import { isSavableToolPart, isArtifactToolPart, TOOL_GENERATE_PRESENTATION, TOOL_PROPOSE_REPORT_EDIT, EXPERIMENTAL_FEATURES_SECURITY_GUID } from './constants';

// A "primary artifact" is a tool part the user actually asked to see — a rendered
// display-tool card (chart/table/…), a generated presentation, or a report-edit
// proposal. Raw MCP activity (query/list_tables) renders as a subtle chip and does
// NOT count. Once one appears in a message, any following prose is a trailing
// explanation that should defer to it rather than bury it.
function isPrimaryArtifactPart(part) {
  if (!part || part.type !== 'tool') return false;
  if (isSavableToolPart(part)) return true;
  // A mockup artifact is the focal card the moment it has content (it renders live while streaming).
  if (isArtifactToolPart(part)) return true;
  return (part.toolName === TOOL_GENERATE_PRESENTATION || part.toolName === TOOL_PROPOSE_REPORT_EDIT)
    && part.state === 'result';
}

// Trailing prose after an artifact: collapsed by default so the card stays the focus.
function TrailingNote({ text }) {
  const [open, setOpen] = React.useState(false);
  return (
    <Box sx={{ mt: 0.5 }}>
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={() => setOpen((o) => !o)}
        sx={{ fontSize: 12, color: 'text.secondary' }}
      >
        {open ? 'Hide explanation' : 'Show explanation'}
      </Link>
      <Collapse in={open} unmountOnExit>
        <Box
          className="ai-chat-markdown"
          sx={{ mt: 0.5, color: 'text.secondary', fontSize: 13, '& p': { m: 0, mb: 1 }, '& :last-child': { mb: 0 } }}
        >
          <Markdown text={text} />
        </Box>
      </Collapse>
    </Box>
  );
}

class ToolRenderErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <Box sx={{ my: 0.75, px: 1.5, py: 0.75, border: '1px solid', borderColor: 'warning.light', borderRadius: 1, color: 'text.secondary', fontSize: 12 }}>
          Unable to render this result.
        </Box>
      );
    }
    return this.props.children;
  }
}

// Wraps a savable tool result with hover-reveal save and enlarge buttons.
// Extracted into its own component so each result has independent `enlarged` state.
function EnlargeableSavableResult({ part, onSaveResult }) {
  const [enlarged, setEnlarged] = React.useState(false);
  const [emailing, setEmailing] = React.useState(false);
  const showToast = useToast();
  const userContext = React.useContext(UserContext);
  const canEmail = Array.isArray(userContext?.securities)
    && userContext.securities.some((s) => s.ua_fid === EXPERIMENTAL_FEATURES_SECURITY_GUID);
  const handleEmail = () => {
    setEmailing(true);
    exportResultToEml(part)
      .then(() => showToast('Email draft (.eml) downloaded — open it to edit and send.', 'success'))
      .catch((err) => showToast(err?.message || 'Failed to export email.', 'error'))
      .finally(() => setEmailing(false));
  };
  return (
    <>
      <Box sx={{ position: 'relative', '&:hover .ai-result-btn': { opacity: 1 } }}>
        <ToolResultRenderer part={part} />
        <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
          <Tooltip title="Enlarge">
            <IconButton
              className="ai-result-btn"
              size="small"
              onClick={() => setEnlarged(true)}
              aria-label="Enlarge visualization"
              sx={{ opacity: 0.35, transition: 'opacity 120ms', bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'background.paper' } }}
            >
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEmail && (
            <Tooltip title="Email this result (.eml)">
              <IconButton
                className="ai-result-btn"
                size="small"
                onClick={handleEmail}
                disabled={emailing}
                aria-label="Email this result"
                sx={{ opacity: 0.35, transition: 'opacity 120ms', bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'background.paper' } }}
              >
                {emailing ? <CircularProgress size={16} /> : <EmailOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          {onSaveResult && (
            <Tooltip title="Save this result">
              <IconButton
                className="ai-result-btn"
                size="small"
                onClick={() => onSaveResult(part)}
                aria-label="Save this result"
                sx={{ opacity: 0.35, transition: 'opacity 120ms', bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'background.paper' } }}
              >
                <BookmarkAddOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Dialog open={enlarged} onClose={() => setEnlarged(false)} fullWidth maxWidth="xl">
        <DialogContent sx={{ minHeight: '65vh', p: 2 }}>
          <ToolResultRenderer part={part} />
        </DialogContent>
      </Dialog>
    </>
  );
}

// One chat message. User = right-aligned bubble. Assistant = left-aligned; its
// parts stream in (markdown text interleaved with tool renderings).
//
// onSaveResult(part): optional. When provided, savable tool results (chart/table/…)
// get a "Save" affordance so the user can capture that single piece as an insight.
//
// isStreaming: this message is the one actively streaming. While true we keep strict
// emit order (nothing collapses or moves, so the live card doesn't jump); once the
// turn completes, prose that follows the first artifact collapses beneath it.
export default function ChatMessage({ message, onSaveResult, isStreaming }) {
  const isUser = message.role === 'user';

  if (isUser) {
    const text = message.parts.filter((p) => p.type === 'text').map((p) => p.text).join('');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 0.75 }}>
        <Paper sx={{ px: 1.5, py: 1, maxWidth: '85%', bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2 }}>
          <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{
      my: 0.75,
      pl: 1.25,
      py: 0.25,
      borderLeft: '3px solid rgba(81,85,213,0.25)',
      bgcolor: 'rgba(81,85,213,0.03)',
      borderRadius: '0 8px 8px 0',
    }}>
      {(() => {
        // Once the turn is complete, prose that appears after the first artifact is a
        // trailing explanation — collapse it so the artifact the user asked for stays
        // the focus. Preamble text (before any artifact) is left at full prominence.
        let sawArtifact = false;
        return message.parts.map((part, i) => {
        if (part.type === 'text') {
          if (!part.text) return null;
          if (sawArtifact && !isStreaming) {
            return <TrailingNote key={i} text={part.text} />;
          }
          return (
            <Box key={i} className="ai-chat-markdown" sx={{ '& p': { m: 0, mb: 1 }, '& :last-child': { mb: 0 }, fontSize: 14 }}>
              <Markdown text={part.text} />
            </Box>
          );
        }
        if (isPrimaryArtifactPart(part)) sawArtifact = true;
        const boundaryKey = part.toolCallId || i;
        const rendered = <ToolResultRenderer part={part} />;
        if (isSavableToolPart(part)) {
          return (
            <ToolRenderErrorBoundary key={boundaryKey}>
              <EnlargeableSavableResult part={part} onSaveResult={onSaveResult || null} />
            </ToolRenderErrorBoundary>
          );
        }
        return <ToolRenderErrorBoundary key={boundaryKey}>{rendered}</ToolRenderErrorBoundary>;
        });
      })()}
    </Box>
  );
}
